import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import clsx from 'clsx';
import { Button, Spinner, useToast } from '@centraoneplus/ui';
import { ticketService, type TicketAccessRequest } from '@services/ticket.service';
import { useAuthStore } from '@stores/authStore';

/** Roles that may ever decide an access request (the server narrows supervisors to the ticket's team). */
const APPROVER_ROLES = ['admin', 'owner', 'supervisor'];
const NOTE_MAX = 500;

function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const message = e?.response?.data?.message;
  return (Array.isArray(message) ? message.join(', ') : message) || e?.message || fallback;
}

interface TicketAccessRequestsBannerProps {
  ticketId: string;
  /** The request the viewer arrived from (notification deep link), shown highlighted. */
  highlightRequestId?: string | null;
  /** Called after an approval, so the page reloads the ticket's secondary agents. */
  onDecided: () => void;
}

/**
 * Ticket detail → pending access requests, for the people who decide them:
 * admins, owners and the supervisor of the ticket's team. Approve adds the
 * requester as a secondary agent; decline can carry a note. Renders nothing
 * for anyone else, or when nothing is pending — the server is the authority
 * (a supervisor of another team gets 403 and sees nothing).
 */
export function TicketAccessRequestsBanner({ ticketId, highlightRequestId, onDecided }: TicketAccessRequestsBannerProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canDecide = !!role && APPROVER_ROLES.includes(role);
  const queryKey = ['ticket-access-requests', ticketId, 'pending'];
  const { data: requests } = useQuery<TicketAccessRequest[]>({
    queryKey,
    queryFn: () => ticketService.getAccessRequests(ticketId, 'pending'),
    enabled: canDecide,
    retry: false,
  });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!canDecide || !requests || requests.length === 0) return null;

  const decide = async (request: TicketAccessRequest, decision: 'approve' | 'reject') => {
    setBusyId(request.id);
    try {
      await ticketService.decideAccessRequest(ticketId, request.id, decision, notes[request.id]?.trim() || undefined);
      if (decision === 'approve') {
        toast.success('Access approved', `${request.requesterName} is now a secondary agent on this ticket.`);
        onDecided();
      } else {
        toast.success('Request declined', `${request.requesterName} has been told.`);
      }
    } catch (err) {
      // e.g. someone else decided it first (409), or the ticket is full (400).
      toast.error('Could not update the request', apiErrorMessage(err, 'Please try again.'));
    } finally {
      setBusyId(null);
      queryClient.invalidateQueries({ queryKey });
    }
  };

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-900/20"
      data-testid="access-requests-banner"
    >
      <p className="mb-2 flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
        <KeyRound className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        {requests.length === 1 ? '1 person is asking for access' : `${requests.length} people are asking for access`}
      </p>
      <ul className="flex flex-col gap-2">
        {requests.map((request) => {
          const busy = busyId === request.id;
          return (
            <li
              key={request.id}
              className={clsx(
                'flex flex-wrap items-center gap-3 rounded-[2px] border bg-white/60 px-3 py-2 dark:bg-gray-900/30',
                request.id === highlightRequestId
                  ? 'border-amber-500 ring-1 ring-amber-400'
                  : 'border-amber-200 dark:border-amber-900/50',
              )}
              data-testid={`access-request-${request.id}`}
            >
              <div className="min-w-[200px] flex-1 text-amber-900 dark:text-amber-100">
                <strong>{request.requesterName}</strong>
                <span className="text-amber-800/80 dark:text-amber-200/70">
                  {' '}
                  asked {new Date(request.createdAt).toLocaleString()}
                </span>
                {request.reason && <p className="mt-0.5 text-[13px]">“{request.reason}”</p>}
              </div>
              <input
                type="text"
                value={notes[request.id] ?? ''}
                onChange={(e) => setNotes((n) => ({ ...n, [request.id]: e.target.value.slice(0, NOTE_MAX) }))}
                maxLength={NOTE_MAX}
                disabled={busy}
                placeholder="Note to them (optional)"
                aria-label={`Note to ${request.requesterName}`}
                className="w-[220px] rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => decide(request, 'reject')} disabled={busy}>
                  Decline
                </Button>
                <Button size="sm" onClick={() => decide(request, 'approve')} disabled={busy}>
                  {busy ? <Spinner size="sm" /> : 'Approve'}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
